import { renderArtifactList } from './artifact-list.js';
import { renderCanvas as renderCanvasView } from './canvas-view.js';
import { renderMemoryList } from './memory-list.js';
import { renderRunList } from './run-list.js';
import { renderSkillList } from './skill-list.js';
import { renderToolCapabilities } from './tool-capabilities.js';
import { formatArtifactTime, memoryStatusLabel, memoryTypeLabel, runStatusLabel } from './workbench-labels.js';

function artifactMetrics(artifact) {
  const preview = artifact.preview || {};
  if (artifact.type === 'research_report') return [['资料', '调研报告'], ['复用', '自动参考']];
  if (artifact.type === 'analysis_brief') {
    return [
      ['价格', preview.price || '—'],
      ['评分', preview.rating != null ? `${preview.rating} ★` : '—'],
      ['评论', preview.reviews ?? '—'],
      ['BSR', preview.bsr ?? '—'],
      ['品牌', preview.brand || '—'],
    ];
  }
  if (artifact.type === 'research_brief') return [['来源', preview.referenceCount ?? '—'], ['状态', '已汇总']];
  return [];
}

function artifactTags(artifact) {
  const preview = artifact.preview || {};
  const tags = [];
  if (artifact.type === 'research_report') tags.push('已自动归类');
  if (preview.asin) tags.push(`ASIN ${preview.asin}`);
  if (preview.marketplace) tags.push(preview.marketplace);
  if (preview.productCount > 1) tags.push(`${preview.productCount} 个商品`);
  if (preview.referenceCount > 0) tags.push(`${preview.referenceCount} 项来源`);
  return tags;
}

/**
 * Keeps workbench DOM rendering in the presentation layer. All user-provided
 * values flow through the individual DOM-node views rather than HTML strings.
 */
export function createWorkbenchRenderer({ elements, syncVisibility, documentRef = document } = {}) {
  const {
    artifactList,
    canvasEmpty,
    skillsItems,
    toolsItems,
    memoryList,
  } = elements || {};

  function renderArtifactState(message, { error = '', retryAction = '' } = {}) {
    if (!artifactList) return;
    const state = documentRef.createElement('div');
    state.className = 'aichat-artifact-empty';
    state.textContent = error ? `${message}：${error}` : message;
    if (retryAction) {
      const retry = documentRef.createElement('button');
      retry.type = 'button';
      retry.dataset.action = retryAction;
      retry.textContent = '重新加载素材';
      state.append(retry);
    }
    artifactList.replaceChildren(state);
  }

  function renderArtifacts({ artifacts = [], loading = false, error = '' } = {}) {
    if (!artifactList) return;
    if (loading) return void renderArtifactState('正在整理本会话的素材…');
    if (error) return void renderArtifactState('素材加载失败', { error, retryAction: 'retry-load-artifacts' });
    if (!artifacts.length) return void renderArtifactState('调研报告、商品分析和联网检索结果会自动整理在这里。需要复用时再加入画布即可。');
    renderArtifactList(artifactList, { artifacts, getMetrics: artifactMetrics, getTags: artifactTags, formatTime: formatArtifactTime });
    syncVisibility?.();
  }

  function renderCanvas({ artifacts = [] } = {}) {
    renderCanvasView(canvasEmpty, { artifacts });
    syncVisibility?.();
  }

  function renderTools({ capabilities = [], loadError = false } = {}) {
    renderToolCapabilities(toolsItems, { capabilities, loadError });
  }

  function renderSkills({ skills = [], loading = false, loadError = false } = {}) {
    renderSkillList(skillsItems, { skills, loading, loadError });
  }

  function renderRuns({ runs = [], loading = false, error = '' } = {}) {
    renderRunList(artifactList, { runs, loading, error, getStatusLabel: runStatusLabel, formatTime: formatArtifactTime });
    syncVisibility?.();
  }

  function renderMemoryItems({ items = [], loading = false, error = '' } = {}) {
    renderMemoryList(memoryList, { items, loading, error, getStatusLabel: memoryStatusLabel, getTypeLabel: memoryTypeLabel, formatTime: formatArtifactTime });
  }

  return Object.freeze({ renderArtifacts, renderCanvas, renderMemoryItems, renderRuns, renderSkills, renderTools });
}
